import { useQuery } from '@tanstack/react-query'
import { createFileRoute, Link } from '@tanstack/react-router'
import { Info, Search, UserSearch } from 'lucide-react'
import { useState } from 'react'
import { toast } from 'sonner'

import { Badge } from '@/components/ui/badge'
import { Input } from '@/components/ui/input'
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet'
import { Skeleton } from '@/components/ui/skeleton'
import { Switch } from '@/components/ui/switch'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  Count,
  EmptyState,
  Page,
  PageHeader,
  Pager,
  SortableHead,
  TableCard,
  Toolbar,
} from '@/components/shell'
import { QuoteAmounts, QuoteBreakdown } from '@/components/quote-amounts'
import { api, type Paginated, type TraderDetail, type TraderRow } from '@/lib/api'
import { formatDateTime, formatUnits, isUnknownQuote, quoteLabel, shortAddress } from '@/lib/format'

type TradersSearch = { address?: string }

export const Route = createFileRoute('/dashboard/traders')({
  validateSearch: (search: Record<string, unknown>): TradersSearch => ({
    address: typeof search.address === 'string' ? search.address : undefined,
  }),
  component: TradersPage,
})

/**
 * 交易者画像。
 *
 * 与「发行人」页的区别：那边看谁发了什么币，这边看谁在买卖。
 * 风控上关注的是「频繁参与已下架代币」的地址——那通常意味着与发行人关联。
 */
function TradersPage() {
  const { address: initialAddress } = Route.useSearch()

  const [page, setPage] = useState(1)
  const [address, setAddress] = useState(initialAddress ?? '')
  const [touchedDelisted, setTouchedDelisted] = useState(false)
  const [sort, setSort] = useState<string | undefined>()
  const [detail, setDetail] = useState<string | null>(initialAddress ?? null)

  const { data, isLoading } = useQuery({
    queryKey: ['traders', { page, address, touchedDelisted, sort }],
    queryFn: () =>
      api.get<Paginated<TraderRow>>('/traders', {
        page,
        perPage: 20,
        address: address || undefined,
        touchedDelisted: touchedDelisted ? 'true' : undefined,
        sort,
      }),
  })

  return (
    <Page>
      <PageHeader
        title="交易者"
        description="按交易地址聚合。「涉及下架」列是风控主信号——频繁买卖已下架代币的地址值得关注。成交额按计价币分列，不同精度的资产不做合计。"
      />

      {/*
        量级提示写在界面上：Trade 表没有 trader 索引，这个页面是全表扫。
        运营看到列表加载慢时应当知道原因，而不是以为系统坏了。
      */}
      <div className="surface-inset flex items-start gap-2 px-4 py-2.5">
        <Info className="mt-px size-3.5 shrink-0 text-mut2" />
        <p className="text-[11.5px] leading-relaxed text-mut2">
          该视图按交易地址实时聚合。当前数据量下响应很快，但索引器表增长后会变慢——
          正式放量前需要在 ignix 侧补 <code className="font-mono">Trade(trader, blockTime)</code>{' '}
          索引。
        </p>
      </div>

      <Toolbar trailing={<Count value={data?.total} unit="个地址" />}>
        <div className="relative">
          <Search className="absolute left-3 top-1/2 size-3.5 -translate-y-1/2 text-mut2" />
          <Input
            value={address}
            onChange={(e) => {
              setAddress(e.target.value)
              setPage(1)
            }}
            placeholder="地址精确搜索"
            className="h-9 w-72 rounded-apple pl-9 font-mono text-[12.5px]"
          />
        </div>

        <label className="flex items-center gap-2 text-[13px] text-mut">
          <Switch
            checked={touchedDelisted}
            onCheckedChange={(v) => {
              setTouchedDelisted(v)
              setPage(1)
            }}
          />
          只看涉及下架代币
        </label>
      </Toolbar>

      <TableCard>
        <Table>
          <TableHeader>
            <TableRow className="border-line/50 hover:bg-transparent">
              <TableHead className="section-label pl-6">地址</TableHead>
              <SortableHead field="tradeCount" sort={sort} onSort={setSort} align="right">
                成交笔数
              </SortableHead>
              <TableHead className="section-label text-right">买 / 卖</TableHead>
              <SortableHead field="tokenCount" sort={sort} onSort={setSort} align="right">
                涉及代币
              </SortableHead>
              <SortableHead field="delistedTokenCount" sort={sort} onSort={setSort} align="right">
                涉及下架
              </SortableHead>
              <TableHead className="section-label text-right">
                成交额
                <span className="ml-1 font-mono text-[9.5px] normal-case text-dim">按计价币</span>
              </TableHead>
              <SortableHead field="lastTradeAt" sort={sort} onSort={setSort} className="pr-6">
                最近交易
              </SortableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading &&
              Array.from({ length: 10 }, (_, i) => (
                <TableRow key={i} className="border-line/40">
                  <TableCell colSpan={7} className="px-6 py-2.5">
                    <Skeleton className="h-7 w-full rounded-apple-sm" />
                  </TableCell>
                </TableRow>
              ))}

            {data?.data.length === 0 && (
              <TableRow className="hover:bg-transparent">
                <TableCell colSpan={7}>
                  <EmptyState icon={UserSearch} title="没有符合条件的交易地址" />
                </TableCell>
              </TableRow>
            )}

            {data?.data.map((t) => (
              <TableRow
                key={t.trader}
                className={`row-hover cursor-pointer border-line/40 ${
                  t.delistedTokenCount > 0 ? 'bg-warn/[0.04]' : ''
                }`}
                onClick={() => setDetail(t.trader)}
              >
                <TableCell className="pl-6">
                  <button
                    className="pressable font-mono text-[11.5px] text-mut hover:text-cyan"
                    onClick={(e) => {
                      e.stopPropagation()
                      navigator.clipboard.writeText(t.trader)
                      toast.success('地址已复制')
                    }}
                    title={t.trader}
                  >
                    {shortAddress(t.trader, 10, 8)}
                  </button>
                </TableCell>
                <TableCell className="tnum text-right font-mono text-[12px] text-txt">
                  {t.tradeCount}
                </TableCell>
                <TableCell className="tnum text-right font-mono text-[11.5px]">
                  <span className="text-up">{t.buyCount}</span>
                  <span className="text-dim"> / </span>
                  <span className="text-dn">{t.sellCount}</span>
                </TableCell>
                <TableCell className="tnum text-right font-mono text-[11.5px] text-mut">
                  {t.tokenCount}
                </TableCell>
                <TableCell className="text-right">
                  {t.delistedTokenCount > 0 ? (
                    <Badge className="h-5 bg-warn/15 text-[10.5px] text-warn hover:bg-warn/15">
                      {t.delistedTokenCount}
                    </Badge>
                  ) : (
                    <span className="tnum font-mono text-[11.5px] text-dim">0</span>
                  )}
                </TableCell>
                <TableCell className="text-right">
                  <QuoteAmounts values={t.volumeByQuote} fractionDigits={3} />
                </TableCell>
                <TableCell className="tnum pr-6 font-mono text-[11px] text-mut2">
                  {formatDateTime(t.lastTradeAt)}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableCard>

      <Pager page={page} pageCount={data?.pageCount ?? 1} onChange={setPage} />

      <TraderDetailSheet address={detail} onClose={() => setDetail(null)} />
    </Page>
  )
}

function TraderDetailSheet({ address, onClose }: { address: string | null; onClose: () => void }) {
  const { data } = useQuery({
    queryKey: ['trader', address],
    // 用服务端的形状，别再手工拼 `TraderRow & { tokens: ... }`——
    // 详情多返回一个字段、或 tokens 里改个名，手拼的那份都不会报错
    queryFn: () => api.get<TraderDetail>(`/traders/${address}`),
    enabled: Boolean(address),
  })

  return (
    <Sheet open={Boolean(address)} onOpenChange={(o) => !o && onClose()}>
      <SheetContent className="flex w-full flex-col gap-0 p-0 sm:max-w-xl">
        <SheetHeader className="border-b border-line/50 px-6 py-5">
          <SheetTitle className="display-title text-2xl">交易者详情</SheetTitle>
          <SheetDescription className="break-all font-mono text-[11.5px]">
            {address}
          </SheetDescription>
        </SheetHeader>

        {!data ? (
          <div className="space-y-2 p-6">
            {Array.from({ length: 5 }, (_, i) => (
              <Skeleton key={i} className="h-10 w-full rounded-apple-sm" />
            ))}
          </div>
        ) : (
          <div className="flex min-h-0 flex-1 flex-col">
            {/*
              金额不进这排数字格：一个地址可能同时在 OKB 与 USD₮0 上有成交，
              塞进一格要么被截断、要么把格子撑歪。分币种账本单独成块，逐行可核对。
            */}
            <div className="grid grid-cols-3 gap-px border-b border-line/50 bg-line/30">
              <Stat label="成交" value={String(data.tradeCount)} />
              <Stat label="代币" value={String(data.tokenCount)} />
              <Stat
                label="涉及下架"
                value={String(data.delistedTokenCount)}
                warn={data.delistedTokenCount > 0}
              />
            </div>

            <div className="border-b border-line/40">
              <QuoteBreakdown
                label="成交额"
                values={data.volumeByQuote}
                fractionDigits={4}
                emptyText="该地址没有可计入的成交金额"
              />
            </div>

            <div className="px-6 py-3 font-mono text-[11px] text-mut2">
              首次 {formatDateTime(data.firstTradeAt)} · 最近 {formatDateTime(data.lastTradeAt)}
            </div>

            <div className="min-h-0 flex-1 overflow-y-auto border-t border-line/40">
              <ul className="divide-y divide-line/40">
                {data.tokens.map((t) => (
                  <li key={t.tokenAddress}>
                    <Link
                      to="/dashboard/tokens/$address"
                      params={{ address: t.tokenAddress }}
                      className="row-hover flex items-center justify-between gap-3 px-6 py-3"
                    >
                      <div className="min-w-0">
                        <div className="flex items-center gap-1.5">
                          <span className="text-[13.5px] text-txt">{t.symbol}</span>
                          {t.hidden && (
                            <Badge className="h-4 bg-warn/15 px-1.5 text-[10px] text-warn hover:bg-warn/15">
                              已下架
                            </Badge>
                          )}
                        </div>
                        <div className="truncate text-[11.5px] text-mut2">{t.name}</div>
                      </div>
                      <div className="shrink-0 text-right">
                        <div className="tnum font-mono text-[11.5px]">
                          <span className="text-up">{t.buyCount}</span>
                          <span className="text-dim"> / </span>
                          <span className="text-dn">{t.sellCount}</span>
                        </div>
                        <div className={`tnum font-mono text-[10.5px] ${netTone(t)}`}>
                          净 {formatUnits(t.netQuoteAmount, t.quoteDecimals, 3)} {quoteLabel(t)}
                        </div>
                      </div>
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        )}
      </SheetContent>
    </Sheet>
  )
}

/**
 * 净额的涨跌染色。
 *
 * `startsWith('-')` 单独用不够：净额恰好为 0（买卖抵平）会落进「非负」分支被染成买入色，
 * 而那是一条中性信息；精度未知时更是连方向都无从谈起，只能保持中性。
 */
function netTone(row: { netQuoteAmount: string; quoteDecimals: number | null }): string {
  if (isUnknownQuote(row) || !/^-?\d+$/.test(row.netQuoteAmount)) return 'text-mut2'
  if (BigInt(row.netQuoteAmount) === 0n) return 'text-mut2'
  return row.netQuoteAmount.startsWith('-') ? 'text-dn' : 'text-up'
}

function Stat({ label, value, warn }: { label: string; value: string; warn?: boolean }) {
  return (
    <div className="bg-void px-4 py-3">
      <div className="section-label">{label}</div>
      <div className={`stat-num mt-1 text-[17px] ${warn ? 'text-warn' : 'text-txt'}`}>{value}</div>
    </div>
  )
}
